import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {Typeahead} from 'react-bootstrap-typeahead'
import 'react-bootstrap-typeahead/css/Typeahead.css'

import {Loading} from '../../../../../../_metronic/elements/Loading'
import {Modal} from '../../../../../../_metronic/elements/Modal'
import {KTSVG} from '../../../../../../_metronic/helpers'
import AppSelect from '../../../../../shared/AppSelect'
import {formatFixedNumber, formatNumber} from '../../../../../shared/numberFormat'
import {useIsMobile} from '../../../../../shared/responsive'
import {createSelectedOptionAwareFilter} from '../../../services/typeaheadFilters'
import {useCreateEconomicReportContext} from './CreateEconomicReportContext'
import {
  getMoistureFieldValidation,
  getOriginalMoistureAssessment,
  getSelectedYieldCorrectionAssessment,
} from './moistureTreatment'

const getLandLabel = ({field, land}, {includeSurface = true} = {}) => {
  const fieldName = field?.name || ''
  const landName = land?.name || land?.label || ''
  const landSurface = Number(String(land?.ha ?? '').replace(',', '.'))
  const surfaceLabel = includeSurface && Number.isFinite(landSurface)
    ? ` - ${formatFixedNumber(landSurface, 2)} ha`
    : ''

  if (fieldName && landName.toLowerCase().startsWith(`${fieldName.toLowerCase()} -`)) {
    return `${landName}${surfaceLabel}`
  }

  return fieldName ? `${fieldName} - ${landName}${surfaceLabel}` : `${landName}${surfaceLabel}`
}

const landTypeaheadFilterBy = createSelectedOptionAwareFilter(getLandLabel)

const getFileExtension = (fileName = '') => {
  const extension = fileName.split('.').pop()
  return extension ? extension.slice(0, 4).toUpperCase() : 'FILE'
}

const getFileSizeLabel = (size = 0) => {
  if (size >= 1024 * 1024) {
    return `${formatFixedNumber(size / (1024 * 1024))} MB`
  }

  return `${formatFixedNumber(Math.max(size / 1024, 0.1))} KB`
}

const UNIT_LABELS = {
  ton_per_ha: 't/ha',
  kg_per_ha: 'kg/ha',
  qq_per_ha: 'qq/ha',
  seeds_per_ha: 'sem/ha',
  thousand_seeds_per_ha: 'mil semillas/ha',
  bags_per_ha: 'bolsas/ha',
  l_per_ha: 'l/ha',
}

const FIXED_COST_LABELS = {
  sowing: 'Siembra',
  spraying: 'Pulverización',
  herbicides: 'Herbicidas',
  harvest: 'Cosecha',
  rent: 'Alquiler',
  agronomist: 'Agrónomo',
  administration: 'Administración',
}

const getDisplayNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const raw = String(value).trim()
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw
  const numericValue = Number(normalized)

  return Number.isFinite(numericValue) ? numericValue : null
}

const sanitizeDecimalInput = (value) => {
  const raw = String(value || '').replace(/[^\d.,]/g, '')

  if (!raw) {
    return ''
  }

  if (raw.includes(',')) {
    const [integerPart, ...decimalParts] = raw.split(',')
    const normalizedInteger = integerPart.replace(/\./g, '')
    const normalizedDecimal = decimalParts.join('').replace(/[^\d]/g, '')

    return `${normalizedInteger}${decimalParts.length > 0 ? `,${normalizedDecimal}` : ''}`
  }

  const [integerPart, ...decimalParts] = raw.split('.')

  if (decimalParts.length < 1) {
    return integerPart
  }

  return `${integerPart},${decimalParts.join('').replace(/[^\d]/g, '')}`
}

const formatMaybe = (value, decimals = 2) => {
  const numericValue = getDisplayNumericValue(value)

  return numericValue === null ? '-' : formatFixedNumber(numericValue, decimals)
}

const getDecimalInputValue = (event) => sanitizeDecimalInput(event.target.value)

const getEditableDecimalValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const raw = String(value).trim()

  if (!raw) {
    return ''
  }

  if (raw.includes(',')) {
    const [integerPart, ...decimalParts] = raw.split(',')
    const normalizedInteger = integerPart.replace(/\./g, '')
    const normalizedDecimal = decimalParts.join('').replace(/[^\d]/g, '')

    return `${normalizedInteger}${decimalParts.length > 0 ? `,${normalizedDecimal}` : ''}`
  }

  return raw.replace(/\./g, '')
}

const formatEditableDecimalValue = (value) => {
  const numericValue = getDisplayNumericValue(value)

  if (numericValue === null) {
    return ''
  }

  return formatNumber(numericValue, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  })
}

const formatMaybeWithUnit = (value, unit = '', decimals = 2) => {
  if (value === null || value === undefined) {
    return '-'
  }

  return `${formatMaybe(value, decimals)}${unit ? ` ${unit}` : ''}`
}

const getFieldInspection = (fields, fieldName) =>
  (fields || []).find((field) => field.name === fieldName) || null

const getFieldStat = (field, camelKey, snakeKey) =>
  field?.[camelKey] ?? field?.[snakeKey] ?? null

const buildExclusiveFieldOptions = (fields, selectedByOtherInputs) => {
  const unavailableFieldNames = new Set(selectedByOtherInputs.filter(Boolean))

  return [
    {label: '-', value: ''},
    ...fields.map((field) => ({
      disabled: unavailableFieldNames.has(field.name),
      label: field.name,
      value: field.name,
    })),
  ]
}

const EconomicNumberInput = ({disabled = false, onChange, value, ...inputProps}) => {
  const [focused, setFocused] = useState(false)
  const [displayValue, setDisplayValue] = useState(() => formatEditableDecimalValue(value))

  useEffect(() => {
    setDisplayValue(focused ? getEditableDecimalValue(value) : formatEditableDecimalValue(value))
  }, [focused, value])

  const handleFocus = () => {
    if (disabled) {
      return
    }

    setFocused(true)
  }

  const handleBlur = () => {
    if (disabled) {
      return
    }

    setFocused(false)
  }

  const handleChange = (event) => {
    const nextValue = getDecimalInputValue(event)

    setDisplayValue(nextValue)
    onChange(nextValue)
  }

  return (
    <input
      {...inputProps}
      type='text'
      inputMode='decimal'
      className='form-control form-control-solid'
      data-economic-number-input='true'
      disabled={disabled}
      value={displayValue}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
    />
  )
}

const blurActiveEconomicNumberInput = (event) => {
  const activeElement = document.activeElement

  if (!activeElement?.matches?.('[data-economic-number-input="true"]')) {
    return
  }

  if (event.target === activeElement) {
    return
  }

  activeElement.blur()
}

const FormHeader = ({mobile = false}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()

  if (mobile) {
    return (
      <div className='reports-mobile-card__header'>
        <button
          type='button'
          className='btn btn-sm btn-light reports-mobile-card__back'
          onClick={() => navigate('/gis/reports/economic')}
        >
          <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
          <span>{t('common:navigation.back_reports')}</span>
        </button>
        <div className='reports-mobile-card__eyebrow'>{t('reports:selector.report_label')}</div>
        <h2 className='reports-mobile-card__title'>{t('reports:economic.create.title')}</h2>
      </div>
    )
  }

  return (
    <div className='modal-header'>
      <h5 className='modal-title'>{t('reports:economic.create.title')}</h5>
    </div>
  )
}

const EconomicReportWizard = ({activeStepIndex, canOpenStep, getStepIsComplete, wizardSteps}) => {
  const {t} = useTranslation()
  const currentStep = wizardSteps[activeStepIndex] || wizardSteps[0]
  const isComplete = getStepIsComplete(currentStep.id)
  const isAvailable = canOpenStep(activeStepIndex)

  return (
    <div className='economic-report-wizard' aria-label={t('reports:economic.create.wizard.title')}>
      <div className='economic-report-wizard__meta'>
        {t('reports:economic.create.wizard.progress', {
          current: activeStepIndex + 1,
          total: wizardSteps.length,
        })}
      </div>
      <div className='economic-report-wizard__track economic-report-wizard__track--current-only'>
        <button
          type='button'
          className={`economic-report-wizard__step is-active ${
            isComplete ? 'is-complete' : ''
          }`.trim()}
          disabled={!isAvailable}
          onClick={() => {}}
          aria-current='step'
        >
          <span>{activeStepIndex + 1}</span>
          <strong>{currentStep.label}</strong>
        </button>
      </div>
    </div>
  )
}

const FormFooter = ({
  activeStepIndex,
  canGoNext,
  isLastStep,
  mobile = false,
  onClose,
  onNext,
  onPrevious,
}) => {
  const {t} = useTranslation()
  const {isFormComplete, loading, onSubmit} = useCreateEconomicReportContext()

  return (
    <div className={mobile ? 'reports-mobile-form__footer' : 'modal-footer'}>
      <button type='button' className='btn btn-light' onClick={onClose} disabled={loading}>
        {t('reports:economic.create.buttons.cancel')}
      </button>
      <div className='economic-report-wizard__footer-actions'>
        {activeStepIndex > 0 && (
          <button type='button' className='btn btn-light' onClick={onPrevious} disabled={loading}>
            {t('reports:economic.create.wizard.previous')}
          </button>
        )}
        {isLastStep ? (
          <button
            onClick={onSubmit}
            disabled={loading || !isFormComplete}
            type='button'
            className={`btn ${isFormComplete ? 'btn-primary' : 'btn-light'}`}
          >
            {t('reports:economic.create.buttons.create')}
          </button>
        ) : (
          <button
            type='button'
            className={`btn ${canGoNext ? 'btn-primary' : 'btn-light'}`}
            onClick={onNext}
            disabled={loading || !canGoNext}
          >
            {t('reports:economic.create.wizard.next')}
          </button>
        )}
      </div>
    </div>
  )
}

const UploadControl = ({kind, label, inspection}) => {
  const {t} = useTranslation()
  const {
    clearFilesForKind,
    inspecting,
    prescriptionFiles,
    removeFileForKind,
    setFilesForKind,
    yieldFiles,
  } = useCreateEconomicReportContext()
  const isInspecting = inspecting === kind
  const isValid = inspection?.valid
  const files = kind === 'prescription' ? prescriptionFiles : yieldFiles
  const featureCount = inspection?.featureCount ?? inspection?.feature_count ?? 0
  const surfaceHa = inspection?.surfaceHa ?? inspection?.surface_ha

  return (
    <div className='economic-report-upload'>
      <div className='economic-report-upload__header'>
        <span className='economic-report-upload__title'>{label}</span>
        {(isInspecting || inspection) && (
          <span
            aria-live='polite'
            className={`badge ${
              isInspecting
                ? 'badge-light-info'
                : isValid
                ? 'badge-light-success'
                : 'badge-light-danger'
            } economic-report-upload__status`}
          >
            {isInspecting && (
              <span
                aria-hidden='true'
                className='spinner-border spinner-border-sm economic-report-upload__spinner'
              />
            )}
            <span>
              {isInspecting
                ? t('reports:economic.create.buttons.inspecting')
                : isValid
                ? 'OK'
                : 'Error'}
            </span>
          </span>
        )}
      </div>
      <label className='economic-report-file-picker'>
        <input
          type='file'
          multiple
          accept='.zip,.shp,.shx,.dbf,.prj,.cpg'
          onChange={(event) => {
            setFilesForKind(kind, event.target.files)
            event.target.value = ''
          }}
        />
        <span className='economic-report-file-picker__copy'>
          <strong>{t('reports:economic.create.buttons.choose_files')}</strong>
          <small>
            {files.length > 0
              ? t(
                  files.length === 1
                ? 'reports:economic.create.hints.file_selected'
                : 'reports:economic.create.hints.files_selected',
                  {count: files.length, displayCount: formatMaybe(files.length, 0)}
                )
              : t('reports:economic.create.hints.file_formats')}
          </small>
        </span>
      </label>
      {files.length > 0 && (
        <div className='economic-report-upload__files'>
          {files.map((file, index) => (
            <div key={`${file.name}-${file.size}-${index}`} className='economic-report-file-row'>
              <span className='economic-report-file-row__ext'>{getFileExtension(file.name)}</span>
              <span className='economic-report-file-row__name'>{file.name}</span>
              <span className='economic-report-file-row__size'>{getFileSizeLabel(file.size)}</span>
              <button
                type='button'
                className='btn btn-sm btn-light economic-report-file-row__remove'
                aria-label={t('reports:economic.create.buttons.remove_file', {name: file.name})}
                onClick={() => removeFileForKind(kind, index)}
              >
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
          ))}
          {files.length > 1 && (
            <button
              type='button'
              className='btn btn-sm btn-light-danger economic-report-upload__clear'
              onClick={() => clearFilesForKind(kind)}
            >
              {t('reports:economic.create.buttons.clear_files')}
            </button>
          )}
        </div>
      )}
      {inspection?.valid && (
        <div className='economic-report-upload__meta'>
          <span>
            {t('reports:economic.create.hints.detected', {
              count: featureCount,
              displayCount: formatMaybe(featureCount, 0),
            })}
          </span>
          {surfaceHa !== null && surfaceHa !== undefined && (
            <span>
              {t('reports:economic.create.hints.surface', {
                value: formatMaybe(surfaceHa),
              })}
            </span>
          )}
        </div>
      )}
      {inspection?.errors?.length > 0 && (
        <div className='economic-report-upload__errors'>
          {inspection.errors.map((error) => (
            <span key={error}>{error}</span>
          ))}
        </div>
      )}
    </div>
  )
}

const GeneralSection = () => {
  const {t} = useTranslation()
  const {campaign, campaignOptions, crop, cropOptions, name, setCampaign, setCrop, setName} =
    useCreateEconomicReportContext()
  const campaignSelectOptions = useMemo(
    () => campaignOptions.map((campaignOption) => ({label: campaignOption, value: campaignOption})),
    [campaignOptions]
  )
  const cropSelectOptions = useMemo(
    () =>
      cropOptions.map((cropOption) => ({
        label: t(`reports:economic.create.inputs.crops.${cropOption}`),
        value: cropOption,
      })),
    [cropOptions, t]
  )

  return (
    <section className='economic-report-section'>
      <h6>{t('reports:economic.create.sections.general')}</h6>
      <label className='economic-report-field'>
        <span>{t('reports:economic.create.inputs.name')}</span>
        <input
          type='text'
          className='form-control form-control-solid'
          value={name}
          maxLength={64}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <div className='economic-report-grid economic-report-grid--two'>
        <AppSelect
          label={t('reports:economic.create.inputs.campaign')}
          onChange={setCampaign}
          options={campaignSelectOptions}
          value={campaign}
        />
        <AppSelect
          label={t('reports:economic.create.inputs.crop')}
          onChange={setCrop}
          options={cropSelectOptions}
          value={crop}
        />
      </div>
    </section>
  )
}

const LandSection = () => {
  const {t} = useTranslation()
  const {landOptions, selectedLand, setSelectedLandId} = useCreateEconomicReportContext()

  return (
    <section className='economic-report-section'>
      <h6>{t('reports:economic.create.sections.land')}</h6>
      <label className='economic-report-field'>
        <span>{t('reports:economic.create.inputs.land')}</span>
        <Typeahead
          id='economic-report-land-search'
          className='economic-report-land-typeahead'
          clearButton
          emptyLabel={t('reports:economic.create.hints.no_lands')}
          filterBy={landTypeaheadFilterBy}
          labelKey={getLandLabel}
          maxResults={200000}
          onChange={(items) => {
            const nextLand = items[0]
            setSelectedLandId(nextLand ? nextLand.land.land_id || nextLand.land.id : '')
          }}
          options={landOptions}
          placeholder={t('reports:economic.create.inputs.land_placeholder')}
          selected={selectedLand ? [selectedLand] : []}
        />
      </label>
    </section>
  )
}

const PrescriptionSection = () => {
  const {t} = useTranslation()
  const {
    environmentFieldName,
    fertilizer1FieldName,
    fertilizer2FieldName,
    prescriptionDoseFieldOptions,
    prescriptionFieldOptions,
    prescriptionInspection,
    seedFieldName,
    setEnvironmentFieldName,
    setFertilizer1FieldName,
    setFertilizer2FieldName,
    setSeedFieldName,
  } = useCreateEconomicReportContext()
  const environmentFieldSelectOptions = useMemo(
    () =>
      buildExclusiveFieldOptions(prescriptionFieldOptions, [
        seedFieldName,
        fertilizer1FieldName,
        fertilizer2FieldName,
      ]),
    [fertilizer1FieldName, fertilizer2FieldName, prescriptionFieldOptions, seedFieldName]
  )
  const seedFieldSelectOptions = useMemo(
    () =>
      buildExclusiveFieldOptions(prescriptionDoseFieldOptions, [
        environmentFieldName,
        fertilizer1FieldName,
        fertilizer2FieldName,
      ]),
    [
      environmentFieldName,
      fertilizer1FieldName,
      fertilizer2FieldName,
      prescriptionDoseFieldOptions,
    ]
  )
  const fertilizer1FieldSelectOptions = useMemo(
    () =>
      buildExclusiveFieldOptions(prescriptionDoseFieldOptions, [
        environmentFieldName,
        seedFieldName,
        fertilizer2FieldName,
      ]),
    [environmentFieldName, fertilizer2FieldName, prescriptionDoseFieldOptions, seedFieldName]
  )
  const fertilizer2FieldSelectOptions = useMemo(
    () =>
      buildExclusiveFieldOptions(prescriptionDoseFieldOptions, [
        environmentFieldName,
        seedFieldName,
        fertilizer1FieldName,
      ]),
    [environmentFieldName, fertilizer1FieldName, prescriptionDoseFieldOptions, seedFieldName]
  )

  return (
    <section className='economic-report-section'>
      <h6>{t('reports:economic.create.sections.prescription')}</h6>
      <UploadControl
        kind='prescription'
        label={t('reports:economic.create.buttons.upload_files')}
        inspection={prescriptionInspection}
      />
      {prescriptionInspection?.valid && (
        <div className='economic-report-grid economic-report-grid--two economic-report-prescription-fields'>
          <AppSelect
            label={t('reports:economic.create.inputs.environment_field')}
            onChange={setEnvironmentFieldName}
            options={environmentFieldSelectOptions}
            value={environmentFieldName}
          />
          <AppSelect
            label={t('reports:economic.create.inputs.seed_field')}
            onChange={setSeedFieldName}
            options={seedFieldSelectOptions}
            value={seedFieldName}
          />
          <AppSelect
            label={t('reports:economic.create.inputs.fertilizer_field', {count: 1})}
            onChange={setFertilizer1FieldName}
            options={fertilizer1FieldSelectOptions}
            value={fertilizer1FieldName}
          />
          <AppSelect
            label={t('reports:economic.create.inputs.fertilizer_field', {count: 2})}
            onChange={setFertilizer2FieldName}
            options={fertilizer2FieldSelectOptions}
            value={fertilizer2FieldName}
          />
        </div>
      )}
    </section>
  )
}

const SurfaceReconciliationSection = () => {
  const {t} = useTranslation()
  const {
    inspecting,
    prescriptionInspection,
    prescriptionSurfaceReference,
    setDeclaredHarvestedSurfaceHa,
    setDeclaredSownSurfaceHa,
    setHarvestStatus,
    setSowingStatus,
    setYieldMapCoverageStatus,
    surfaceReconciliation,
    surfaceReconciliationAssessment,
    yieldFiles,
    yieldInspection,
  } = useCreateEconomicReportContext()
  const responseOptions = useMemo(
    () => [
      {
        label: t('reports:economic.create.surface_reconciliation.options.yes'),
        value: 'yes',
      },
      {
        label: t('reports:economic.create.surface_reconciliation.options.no'),
        value: 'no',
      },
      {
        label: t('reports:economic.create.surface_reconciliation.options.unknown'),
        value: 'unknown',
      },
    ],
    [t]
  )
  const sowingOptions = useMemo(
    () =>
      responseOptions.map((option) => ({
        ...option,
        value:
          option.value === 'yes'
            ? 'matches_prescription'
            : option.value === 'no'
            ? 'differs'
            : 'unknown',
      })),
    [responseOptions]
  )
  const harvestOptions = useMemo(
    () =>
      responseOptions.map((option) => ({
        ...option,
        value:
          option.value === 'yes'
            ? 'matches_sown'
            : option.value === 'no'
            ? 'differs'
            : 'unknown',
      })),
    [responseOptions]
  )
  const coverageOptions = useMemo(
    () =>
      responseOptions.map((option) => ({
        ...option,
        value:
          option.value === 'yes'
            ? 'covers_harvest'
            : option.value === 'no'
            ? 'partial'
            : 'unknown',
      })),
    [responseOptions]
  )

  if (!prescriptionInspection?.valid) {
    return null
  }

  const referenceMessage =
    prescriptionSurfaceReference.kind === 'inside_land' &&
    prescriptionSurfaceReference.outsideSurfaceHa > 0
      ? t(
          'reports:economic.create.surface_reconciliation.reference_inside_land_with_outside',
          {
            outsideValue: formatMaybe(
              prescriptionSurfaceReference.outsideSurfaceHa
            ),
            value: formatMaybe(prescriptionSurfaceReference.surfaceHa),
          }
        )
      : prescriptionSurfaceReference.kind === 'inside_land'
      ? t(
          'reports:economic.create.surface_reconciliation.reference_inside_land',
          {value: formatMaybe(prescriptionSurfaceReference.surfaceHa)}
        )
      : prescriptionSurfaceReference.kind === 'uploaded_pending_clip'
      ? t(
          'reports:economic.create.surface_reconciliation.reference_uploaded_pending_clip',
          {value: formatMaybe(prescriptionSurfaceReference.surfaceHa)}
        )
      : t(
          'reports:economic.create.surface_reconciliation.reference_pending'
        )
  const sownSurfaceError =
    surfaceReconciliationAssessment.errors.declaredSownSurfaceHa
  const harvestedSurfaceError =
    surfaceReconciliationAssessment.errors.declaredHarvestedSurfaceHa
  const isYieldMapReady =
    yieldFiles.length > 0 &&
    yieldInspection?.valid === true &&
    inspecting !== 'yield_map'
  const canAnswerHarvest =
    isYieldMapReady && surfaceReconciliationAssessment.sowingValid
  const canAnswerCoverage =
    isYieldMapReady && surfaceReconciliationAssessment.harvestValid

  return (
    <div
      aria-disabled={!isYieldMapReady}
      className={`economic-report-yield-subsection economic-report-surface-reconciliation ${
        isYieldMapReady ? '' : 'economic-report-surface-reconciliation--disabled'
      }`.trim()}
    >
      <div className='economic-report-yield-subsection__heading'>
        <strong>
          {t('reports:economic.create.surface_reconciliation.title')}
        </strong>
        <small>
          {t('reports:economic.create.surface_reconciliation.description')}
        </small>
      </div>

      {!isYieldMapReady && (
        <small className='economic-report-help'>
          {t(
            'reports:economic.create.surface_reconciliation.yield_map_waiting'
          )}
        </small>
      )}

      <div className='economic-report-surface-question'>
        <AppSelect
          disabled={!isYieldMapReady}
          label={t(
            'reports:economic.create.surface_reconciliation.sowing_question'
          )}
          onChange={setSowingStatus}
          options={sowingOptions}
          placeholder={t(
            'reports:economic.create.surface_reconciliation.select_response'
          )}
          value={surfaceReconciliation.sowingStatus}
        />
        {surfaceReconciliation.sowingStatus === 'differs' && (
          <label className='economic-report-field economic-report-surface-input'>
            <span>
              {t(
                'reports:economic.create.surface_reconciliation.declared_sown_surface'
              )}
            </span>
            <EconomicNumberInput
              aria-describedby={
                sownSurfaceError ? 'surface-reconciliation-sown-error' : undefined
              }
              aria-invalid={Boolean(sownSurfaceError)}
              disabled={!isYieldMapReady}
              onChange={setDeclaredSownSurfaceHa}
              value={surfaceReconciliation.declaredSownSurfaceHa}
            />
            {sownSurfaceError && (
              <small
                className='economic-report-help economic-report-help--error'
                id='surface-reconciliation-sown-error'
              >
                {t(
                  'reports:economic.create.surface_reconciliation.errors.positive_surface'
                )}
              </small>
            )}
          </label>
        )}
      </div>

      {canAnswerHarvest && (
        <div className='economic-report-surface-question'>
          <AppSelect
            label={t(
              'reports:economic.create.surface_reconciliation.harvest_question'
            )}
            onChange={setHarvestStatus}
            options={harvestOptions}
            placeholder={t(
              'reports:economic.create.surface_reconciliation.select_response'
            )}
            value={surfaceReconciliation.harvestStatus}
          />
          {surfaceReconciliation.harvestStatus === 'differs' && (
            <label className='economic-report-field economic-report-surface-input'>
              <span>
                {t(
                  'reports:economic.create.surface_reconciliation.declared_harvested_surface'
                )}
              </span>
              <EconomicNumberInput
                aria-describedby={
                  harvestedSurfaceError
                    ? 'surface-reconciliation-harvest-error'
                    : undefined
                }
                aria-invalid={Boolean(harvestedSurfaceError)}
                onChange={setDeclaredHarvestedSurfaceHa}
                value={surfaceReconciliation.declaredHarvestedSurfaceHa}
              />
              {harvestedSurfaceError && (
                <small
                  className='economic-report-help economic-report-help--error'
                  id='surface-reconciliation-harvest-error'
                >
                  {t(
                    `reports:economic.create.surface_reconciliation.errors.${harvestedSurfaceError}`,
                    {
                      value: formatMaybe(
                        surfaceReconciliationAssessment.resolvedSownSurfaceHa
                      ),
                    }
                  )}
                </small>
              )}
            </label>
          )}
        </div>
      )}

      {canAnswerCoverage && (
        <div className='economic-report-surface-question'>
          <AppSelect
            label={t(
              'reports:economic.create.surface_reconciliation.yield_map_coverage_question'
            )}
            onChange={setYieldMapCoverageStatus}
            options={coverageOptions}
            placeholder={t(
              'reports:economic.create.surface_reconciliation.select_response'
            )}
            value={surfaceReconciliation.yieldMapCoverageStatus}
          />
        </div>
      )}

      <div
        className={`economic-report-surface-notices economic-report-surface-notices--${prescriptionSurfaceReference.kind}`}
        role='note'
      >
        <span>{referenceMessage}</span>
        <span>
          {t(
            'reports:economic.create.surface_reconciliation.yield_map_coverage_note'
          )}
        </span>
      </div>

    </div>
  )
}

const YieldMapSection = ({part = 'fields'}) => {
  const {t} = useTranslation()
  const {
    calibrateYieldMap,
    calibrationConfirmed,
    calibrationSourceConfirmed,
    changeOriginalMoistureField,
    cleanYieldMap,
    cleanYieldConfirmed,
    inspecting,
    moistureTreatment,
    originalMoistureConfirmed,
    rawMoistureAssessment,
    rawYieldConfirmed,
    selectedYieldCorrectionAssessment,
    setCalibrateYieldMap,
    setCalibrationSourceConfirmed,
    setCalibrationConfirmed,
    setCleanYieldMap,
    setCleanYieldConfirmed,
    setMoistureTreatment,
    setOriginalMoistureConfirmed,
    setRawYieldConfirmed,
    setYieldCorrection,
    changeYieldFieldName,
    setYieldUnit,
    yieldCorrection,
    yieldFieldName,
    yieldFieldOptions,
    yieldInspection,
    yieldUnit,
    yieldUnitOptions,
  } = useCreateEconomicReportContext()
  const isYieldInspecting = inspecting === 'yield_map'
  const yieldFieldSelectOptions = useMemo(
    () => [
      {label: '-', value: ''},
      ...yieldFieldOptions.map((field) => {
        const assessment = getSelectedYieldCorrectionAssessment(
          yieldInspection,
          field.name
        )
        const correctedLabel = assessment.suggestedField
          ? t('reports:economic.create.yield_field_status.corrected_with_source', {
              field: assessment.suggestedField,
            })
          : t('reports:economic.create.yield_field_status.corrected')

        return {
          disabled:
            field.name === moistureTreatment.originalMoistureField ||
            !assessment.valid,
          label: assessment.valid
            ? field.name
            : `${field.name} - ${correctedLabel}`,
          value: field.name,
        }
      }),
    ],
    [
      moistureTreatment.originalMoistureField,
      t,
      yieldFieldOptions,
      yieldInspection,
    ]
  )
  const yieldUnitSelectOptions = useMemo(
    () => yieldUnitOptions.map((unit) => ({label: UNIT_LABELS[unit] || unit, value: unit})),
    [yieldUnitOptions]
  )
  const moistureFieldOptions = useMemo(
    () => [
      {label: '-', value: ''},
      ...(yieldInspection?.fields || []).map((field) => {
        const validation = getMoistureFieldValidation(
          yieldInspection,
          field.name,
          yieldFieldName
        )
        const originalAssessment = getOriginalMoistureAssessment(
          yieldInspection,
          field.name,
          yieldFieldName
        )
        const statusReason = validation.valid
          ? originalAssessment.reason
          : validation.reason

        return {
          disabled: !validation.valid || !originalAssessment.valid,
          label: validation.valid && originalAssessment.valid
            ? field.name
            : `${field.name} - ${t(
                `reports:economic.create.moisture_field_status.${statusReason}`,
                {value: originalAssessment.targetPercent}
              )}`,
          value: field.name,
        }
      }),
    ],
    [t, yieldFieldName, yieldInspection]
  )
  const yieldFieldStats = useMemo(
    () => getFieldInspection(yieldInspection?.fields || [], yieldFieldName),
    [yieldFieldName, yieldInspection]
  )
  const moistureFieldStats = useMemo(
    () =>
      getFieldInspection(
        yieldInspection?.fields || [],
        moistureTreatment.originalMoistureField
      ),
    [moistureTreatment.originalMoistureField, yieldInspection]
  )
  const yieldUnitLabel = UNIT_LABELS[yieldUnit] || yieldUnit
  const hasValidYieldMap = yieldInspection?.valid === true
  const hasSelectedYieldField = Boolean(yieldFieldName)
  const yieldFieldControlsDisabled =
    isYieldInspecting || !hasValidYieldMap || !hasSelectedYieldField
  const yieldUnitDisabled = isYieldInspecting || !hasSelectedYieldField
  const rawInputReady =
    !yieldFieldControlsDisabled &&
    selectedYieldCorrectionAssessment.valid &&
    rawYieldConfirmed &&
    Boolean(moistureTreatment.originalMoistureField) &&
    rawMoistureAssessment.valid &&
    originalMoistureConfirmed
  const yieldControlsDisabled = !rawInputReady
  const cleaningButtonDisabled = yieldControlsDisabled || cleanYieldConfirmed
  const calibrationButtonDisabled = yieldControlsDisabled || calibrationConfirmed
  const updateCorrection = (key, value) => {
    setYieldCorrection((current) => ({...current, [key]: value}))
  }
  const updateMoistureTreatment = (key, value) => {
    setMoistureTreatment((current) => ({...current, [key]: value}))
  }
  const updateCleanYieldConfirmation = (checked) => {
    if (checked) {
      setCleanYieldMap(false)
      setYieldCorrection((current) => ({
        ...current,
        maxYieldTonsHa: '',
        minYieldTonsHa: '',
      }))
    }

    setCleanYieldConfirmed(checked)
  }
  const updateCalibrationConfirmation = (checked) => {
    if (checked) {
      setCalibrateYieldMap(false)
      setCalibrationSourceConfirmed(false)
      setYieldCorrection((current) => ({
        ...current,
        totalHarvestedTons: '',
      }))
    }

    setCalibrationConfirmed(checked)
  }
  const returnToCleaningDecision = () => {
    setCleanYieldMap(false)
    setCleanYieldConfirmed(false)
    setYieldCorrection((current) => ({
      ...current,
      maxYieldTonsHa: '',
      minYieldTonsHa: '',
    }))
  }
  const returnToCalibrationDecision = () => {
    setCalibrateYieldMap(false)
    setCalibrationConfirmed(false)
    setCalibrationSourceConfirmed(false)
    setYieldCorrection((current) => ({
      ...current,
      totalHarvestedTons: '',
    }))
  }

  return (
    <section className='economic-report-section'>
      {part === 'fields' && (
        <>
          <h6>{t('reports:economic.create.sections.yield_map')}</h6>
          <UploadControl
            kind='yield_map'
            label={t('reports:economic.create.buttons.upload_files')}
            inspection={yieldInspection}
          />
          {yieldFieldOptions.length > 0 && (
            <div className='economic-report-yield-field-group'>
          <div className='economic-report-grid economic-report-grid--two'>
            <AppSelect
              disabled={isYieldInspecting}
              label={t('reports:economic.create.inputs.yield_field')}
              onChange={changeYieldFieldName}
              options={yieldFieldSelectOptions}
              value={yieldFieldName}
            />
            <AppSelect
              disabled={yieldUnitDisabled}
              label={t('reports:economic.create.inputs.yield_unit')}
              onChange={setYieldUnit}
              options={yieldUnitSelectOptions}
              value={yieldUnit}
            />
          </div>
          {yieldFieldName && (
            <div className='economic-report-yield-stats economic-report-yield-stats--yield'>
              <span>
                <small>{t('reports:economic.create.inputs.yield_average')}</small>
                <strong>
                  {formatMaybeWithUnit(
                    getFieldStat(yieldFieldStats, 'averageValue', 'average_value'),
                    yieldUnitLabel
                  )}
                </strong>
              </span>
              <span>
                <small>{t('reports:economic.create.inputs.yield_min_file')}</small>
                <strong>
                  {formatMaybeWithUnit(
                    getFieldStat(yieldFieldStats, 'minValue', 'min_value'),
                    yieldUnitLabel
                  )}
                </strong>
              </span>
              <span>
                <small>{t('reports:economic.create.inputs.yield_max_file')}</small>
                <strong>
                  {formatMaybeWithUnit(
                    getFieldStat(yieldFieldStats, 'maxValue', 'max_value'),
                    yieldUnitLabel
                  )}
                </strong>
              </span>
            </div>
          )}
          {yieldFieldName && !selectedYieldCorrectionAssessment.valid && (
            <small className='economic-report-help economic-report-help--error'>
              {t('reports:economic.create.errors.corrected_yield_field', {
                field: selectedYieldCorrectionAssessment.suggestedField || '-',
              })}
            </small>
          )}
          <label
            className={`economic-report-check ${
              yieldFieldControlsDisabled ||
              !selectedYieldCorrectionAssessment.valid
                ? 'is-disabled'
                : ''
            }`.trim()}
          >
            <input
              type='checkbox'
              checked={rawYieldConfirmed}
              disabled={
                yieldFieldControlsDisabled ||
                !selectedYieldCorrectionAssessment.valid
              }
              onChange={(event) => setRawYieldConfirmed(event.target.checked)}
            />
            <span>{t('reports:economic.create.inputs.raw_yield_confirmation')}</span>
          </label>
            </div>
          )}
          {yieldFieldOptions.length > 0 && (
            <div className='economic-report-yield-field-group'>
          <AppSelect
            disabled={
              yieldFieldControlsDisabled ||
              !selectedYieldCorrectionAssessment.valid ||
              !rawYieldConfirmed
            }
            label={t('reports:economic.create.inputs.original_moisture_field')}
            onChange={changeOriginalMoistureField}
            options={moistureFieldOptions}
            value={moistureTreatment.originalMoistureField}
          />
          {moistureTreatment.originalMoistureField && (
            <div className='economic-report-yield-stats economic-report-yield-stats--moisture'>
              <span>
                <small>{t('reports:economic.create.inputs.moisture_min_file')}</small>
                <strong>
                  {formatMaybeWithUnit(
                    getFieldStat(moistureFieldStats, 'minValue', 'min_value'),
                    '%'
                  )}
                </strong>
              </span>
              <span>
                <small>{t('reports:economic.create.inputs.moisture_max_file')}</small>
                <strong>
                  {formatMaybeWithUnit(
                    getFieldStat(moistureFieldStats, 'maxValue', 'max_value'),
                    '%'
                  )}
                </strong>
              </span>
            </div>
          )}
          {moistureTreatment.originalMoistureField &&
            !rawMoistureAssessment.valid && (
              <small className='economic-report-help economic-report-help--error'>
                {t(
                  rawMoistureAssessment.reason === 'uniform_corrected'
                    ? 'reports:economic.create.errors.uniform_moisture_field'
                    : rawMoistureAssessment.reason === 'corrected_at_target'
                    ? 'reports:economic.create.errors.corrected_moisture_field'
                    : 'reports:economic.create.errors.moisture_field',
                  {value: rawMoistureAssessment.targetPercent}
                )}
              </small>
            )}
          <label
            className={`economic-report-check ${
              !moistureTreatment.originalMoistureField ||
              !rawMoistureAssessment.valid
                ? 'is-disabled'
                : ''
            }`.trim()}
          >
            <input
              type='checkbox'
              checked={originalMoistureConfirmed}
              disabled={
                !moistureTreatment.originalMoistureField ||
                !rawMoistureAssessment.valid
              }
              onChange={(event) =>
                setOriginalMoistureConfirmed(event.target.checked)
              }
            />
            <span>
              {t('reports:economic.create.inputs.original_moisture_confirmation')}
            </span>
          </label>
          {hasValidYieldMap &&
            !moistureFieldOptions.some((option) => option.value && !option.disabled) && (
              <small className='economic-report-help economic-report-help--error'>
                {t('reports:economic.create.errors.no_valid_moisture_field')}
              </small>
            )}
            </div>
          )}
        </>
      )}

      {part === 'surfaces' && <SurfaceReconciliationSection />}

      {part === 'corrections' && (
        <>
          <div className='economic-report-yield-subsection'>
        <div className='economic-report-yield-subsection__heading'>
          <strong>{t('reports:economic.create.sections.extreme_cleaning')}</strong>
          <small>{t('reports:economic.create.hints.extreme_cleaning')}</small>
        </div>
        {!cleanYieldMap && (
          <div className='economic-report-yield-actions'>
            <button
              type='button'
              className='btn btn-light-primary economic-report-yield-correction-button'
              disabled={cleaningButtonDisabled}
              onClick={() => {
                setCleanYieldConfirmed(false)
                setCleanYieldMap(true)
              }}
            >
              {t('reports:economic.create.inputs.clean_yield_map')}
            </button>
          </div>
        )}
        {cleanYieldMap && (
          <>
            <div className='economic-report-yield-actions'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary btn-icon economic-report-yield-back-button'
                aria-label={t('reports:economic.create.inputs.back_to_cleaning_decision')}
                title={t('reports:economic.create.inputs.back_to_cleaning_decision')}
                onClick={returnToCleaningDecision}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr063.svg'
                  className='svg-icon svg-icon-2'
                />
              </button>
            </div>
            <div className='economic-report-correction-grid economic-report-correction-grid--two'>
              <label
                className={`economic-report-field ${
                  yieldControlsDisabled ? 'is-disabled' : ''
                }`.trim()}
              >
                <span>{t('reports:economic.create.inputs.min_yield')}</span>
                <EconomicNumberInput
                  disabled={yieldControlsDisabled}
                  value={yieldCorrection.minYieldTonsHa}
                  onChange={(value) => updateCorrection('minYieldTonsHa', value)}
                />
              </label>
              <label
                className={`economic-report-field ${
                  yieldControlsDisabled ? 'is-disabled' : ''
                }`.trim()}
              >
                <span>{t('reports:economic.create.inputs.max_yield')}</span>
                <EconomicNumberInput
                  disabled={yieldControlsDisabled}
                  value={yieldCorrection.maxYieldTonsHa}
                  onChange={(value) => updateCorrection('maxYieldTonsHa', value)}
                />
              </label>
            </div>
          </>
        )}
        {!cleanYieldMap && (
          <label
            className={`economic-report-check ${yieldControlsDisabled ? 'is-disabled' : ''}`.trim()}
          >
            <input
              type='checkbox'
              checked={cleanYieldConfirmed}
              disabled={yieldControlsDisabled}
              onChange={(event) => updateCleanYieldConfirmation(event.target.checked)}
            />
            <span>{t('reports:economic.create.inputs.clean_yield')}</span>
          </label>
        )}
          </div>

          <div className='economic-report-yield-subsection'>
        <div className='economic-report-yield-subsection__heading'>
          <strong>{t('reports:economic.create.sections.calibration')}</strong>
          <small>{t('reports:economic.create.hints.calibration')}</small>
        </div>
        {!calibrateYieldMap && (
          <div className='economic-report-yield-actions'>
            <button
              type='button'
              className='btn btn-light-primary economic-report-yield-correction-button'
              disabled={calibrationButtonDisabled}
              onClick={() => {
                setCalibrationConfirmed(false)
                setCalibrateYieldMap(true)
              }}
            >
              {t('reports:economic.create.inputs.calibrate_yield_map')}
            </button>
          </div>
        )}
        {calibrateYieldMap && (
          <>
            <div className='economic-report-yield-actions'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary btn-icon economic-report-yield-back-button'
                aria-label={t('reports:economic.create.inputs.back_to_calibration_decision')}
                title={t('reports:economic.create.inputs.back_to_calibration_decision')}
                onClick={returnToCalibrationDecision}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr063.svg'
                  className='svg-icon svg-icon-2'
                />
              </button>
            </div>
            <div className='economic-report-correction-grid economic-report-correction-grid--single'>
              <label
                className={`economic-report-field ${
                  yieldControlsDisabled ? 'is-disabled' : ''
                }`.trim()}
              >
                <span>{t('reports:economic.create.inputs.total_harvested')}</span>
                <EconomicNumberInput
                  disabled={yieldControlsDisabled}
                  value={yieldCorrection.totalHarvestedTons}
                  onChange={(value) => {
                    updateCorrection('totalHarvestedTons', value)
                    setCalibrationSourceConfirmed(false)
                  }}
                />
              </label>
            </div>
            <label
              className={`economic-report-check ${
                yieldControlsDisabled ||
                !yieldCorrection.totalHarvestedTons
                  ? 'is-disabled'
                  : ''
              }`.trim()}
            >
              <input
                type='checkbox'
                checked={calibrationSourceConfirmed}
                disabled={
                  yieldControlsDisabled ||
                  !yieldCorrection.totalHarvestedTons
                }
                onChange={(event) =>
                  setCalibrationSourceConfirmed(event.target.checked)
                }
              />
              <span>
                {t(
                  'reports:economic.create.inputs.calibration_source_confirmation'
                )}
              </span>
            </label>
          </>
        )}
        {!calibrateYieldMap && (
          <label
            className={`economic-report-check ${yieldControlsDisabled ? 'is-disabled' : ''}`.trim()}
          >
            <input
              type='checkbox'
              checked={calibrationConfirmed}
              disabled={yieldControlsDisabled}
              onChange={(event) => updateCalibrationConfirmation(event.target.checked)}
            />
            <span>{t('reports:economic.create.inputs.calibrated_yield')}</span>
          </label>
        )}
          </div>

          <div className='economic-report-yield-subsection'>
        <div className='economic-report-yield-subsection__heading'>
          <strong>{t('reports:economic.create.sections.moisture_treatment')}</strong>
          <small>{t('reports:economic.create.hints.moisture_required')}</small>
        </div>
        <label
          className={`economic-report-field ${
            yieldControlsDisabled ? 'is-disabled' : ''
          }`.trim()}
        >
          <span>{t('reports:economic.create.inputs.output_commercial_moisture')}</span>
          <EconomicNumberInput
            disabled={yieldControlsDisabled}
            value={moistureTreatment.outputCommercialTargetPercent}
            onChange={(value) =>
              updateMoistureTreatment('outputCommercialTargetPercent', value)
            }
          />
        </label>
        <div className='economic-report-moisture-policies'>
          <span>{t('reports:economic.create.hints.moisture_treatment')}</span>
        </div>
          </div>
        </>
      )}
    </section>
  )
}

const CostsSection = () => {
  const {t} = useTranslation()
  const {
    detectedFertilizer1,
    detectedFertilizer2,
    environmentCostPreview,
    fertilizer1Enabled,
    fertilizer1Price,
    fertilizer1Unit,
    fertilizer2Enabled,
    fertilizer2Price,
    fertilizer2Unit,
    fertilizerUnitOptions,
    fixedCostKeys,
    fixedCostTotal,
    fixedCostTotalValue,
    fixedCosts,
    netPrice,
    seedBagPrice,
    seedSeedsPerBag,
    seedUnit,
    seedUnitOptions,
    setFertilizer1Price,
    setFertilizer1Unit,
    setFertilizer2Price,
    setFertilizer2Unit,
    setFixedCostTotalInput,
    setFixedCosts,
    setNetPrice,
    setSeedBagPrice,
    setSeedSeedsPerBag,
    setSeedUnit,
  } = useCreateEconomicReportContext()
  const seedUnitSelectOptions = useMemo(
    () => seedUnitOptions.map((unit) => ({label: UNIT_LABELS[unit] || unit, value: unit})),
    [seedUnitOptions]
  )
  const fertilizerUnitSelectOptions = useMemo(
    () => fertilizerUnitOptions.map((unit) => ({label: UNIT_LABELS[unit] || unit, value: unit})),
    [fertilizerUnitOptions]
  )
  const updateFixedCost = (key, value) => {
    setFixedCostTotalInput('')
    setFixedCosts((current) => ({...current, [key]: value}))
  }
  const updateFixedCostTotal = (value) => {
    setFixedCosts(
      fixedCostKeys.reduce((nextFixedCosts, key) => {
        nextFixedCosts[key] = ''
        return nextFixedCosts
      }, {})
    )
    setFixedCostTotalInput(value)
  }

  return (
    <section className='economic-report-section'>
      <h6>{t('reports:economic.create.sections.costs')}</h6>
      <div className='economic-report-cost-builder'>
        <div className='economic-report-cost-builder__group'>
          <label className='economic-report-field'>
            <span>{t('reports:economic.create.inputs.net_price')}</span>
            <EconomicNumberInput value={netPrice} onChange={setNetPrice} />
            <small className='economic-report-help'>
              {t('reports:economic.create.hints.net_price')}
            </small>
          </label>
        </div>
        <div className='economic-report-cost-builder__group'>
          <h6>{t('reports:economic.create.inputs.seed_cost')}</h6>
          <div className='economic-report-grid economic-report-grid--three'>
            <AppSelect
              label={t('reports:economic.create.inputs.seed_unit')}
              onChange={setSeedUnit}
              options={seedUnitSelectOptions}
              value={seedUnit}
            />
            <label className='economic-report-field'>
              <span>{t('reports:economic.create.inputs.seeds_per_bag')}</span>
              <EconomicNumberInput
                disabled={seedUnit === 'bags_per_ha'}
                value={seedSeedsPerBag}
                onChange={setSeedSeedsPerBag}
              />
            </label>
            <label className='economic-report-field'>
              <span>{t('reports:economic.create.inputs.seed_bag_price')}</span>
              <EconomicNumberInput value={seedBagPrice} onChange={setSeedBagPrice} />
            </label>
          </div>
        </div>
        {fertilizer1Enabled && (
          <div className='economic-report-cost-builder__group'>
            <h6>
              {t('reports:economic.create.inputs.fertilizer_cost', {
                count: 1,
                field: detectedFertilizer1?.field,
              })}
            </h6>
            <div className='economic-report-grid economic-report-grid--two'>
              <AppSelect
                label={t('reports:economic.create.inputs.fertilizer_unit')}
                onChange={setFertilizer1Unit}
                options={fertilizerUnitSelectOptions}
                value={fertilizer1Unit}
              />
              <label className='economic-report-field'>
                <span>
                  {fertilizer1Unit === 'l_per_ha'
                    ? t('reports:economic.create.inputs.fertilizer_price_l')
                    : t('reports:economic.create.inputs.fertilizer_price_t')}
                </span>
                <EconomicNumberInput value={fertilizer1Price} onChange={setFertilizer1Price} />
              </label>
            </div>
          </div>
        )}
        {fertilizer2Enabled && (
          <div className='economic-report-cost-builder__group'>
            <h6>
              {t('reports:economic.create.inputs.fertilizer_cost', {
                count: 2,
                field: detectedFertilizer2?.field,
              })}
            </h6>
            <div className='economic-report-grid economic-report-grid--two'>
              <AppSelect
                label={t('reports:economic.create.inputs.fertilizer_unit')}
                onChange={setFertilizer2Unit}
                options={fertilizerUnitSelectOptions}
                value={fertilizer2Unit}
              />
              <label className='economic-report-field'>
                <span>
                  {fertilizer2Unit === 'l_per_ha'
                    ? t('reports:economic.create.inputs.fertilizer_price_l')
                    : t('reports:economic.create.inputs.fertilizer_price_t')}
                </span>
                <EconomicNumberInput value={fertilizer2Price} onChange={setFertilizer2Price} />
              </label>
            </div>
          </div>
        )}
        <div className='economic-report-cost-builder__group'>
          <h6>{t('reports:economic.create.inputs.fixed_costs')}</h6>
          <small className='economic-report-help'>
            {t('reports:economic.create.hints.fixed_costs')}
          </small>
          <div className='economic-report-grid economic-report-grid--fixed-costs'>
            {fixedCostKeys.map((key) => (
              <label key={key} className='economic-report-field'>
                <span>{FIXED_COST_LABELS[key]}</span>
                <EconomicNumberInput
                  value={fixedCosts[key]}
                  onChange={(value) => updateFixedCost(key, value)}
                />
              </label>
            ))}
          </div>
          <div className='economic-report-fixed-cost-total'>
            <label className='economic-report-field'>
              <span>{t('reports:economic.create.inputs.fixed_cost_total')}</span>
              <EconomicNumberInput
                value={fixedCostTotalValue}
                onChange={updateFixedCostTotal}
              />
            </label>
          </div>
        </div>
      </div>
      <EnvironmentCostPreview rows={environmentCostPreview} fixedCostTotal={fixedCostTotal} />
    </section>
  )
}

const EnvironmentCostPreview = ({rows, fixedCostTotal}) => {
  const {t} = useTranslation()

  if (rows.length < 1) {
    return (
      <div className='economic-report-empty-hint'>
        {t('reports:economic.create.hints.costs_waiting')}
      </div>
    )
  }

  return (
    <div className='economic-report-review-table'>
      <div className='economic-report-review-table__header'>
        <span>{t('reports:economic.create.review.environment')}</span>
        <span>{t('reports:economic.create.review.seed')}</span>
        <span>{t('reports:economic.create.review.fertilizers')}</span>
        <span>{t('reports:economic.create.review.variable_cost')}</span>
        <span>{t('reports:economic.create.review.fixed_cost')}</span>
        <span>{t('reports:economic.create.review.total_cost')}</span>
      </div>
      {rows.map((row) => (
        <div className='economic-report-review-table__row' key={row.environmentId || row.environmentName}>
          <strong>{row.environmentName || row.environmentId}</strong>
          <span>{formatMaybe(row.seedDose)}</span>
          <span>
            {[row.fertilizer1Dose, row.fertilizer2Dose]
              .filter((value) => value !== null && value !== undefined)
              .map((value) => formatMaybe(value))
              .join(' / ') || '-'}
          </span>
          <span>{formatMaybe(row.variableCostUsdHa)} USD/ha</span>
          <span>{formatMaybe(row.fixedCostUsdHa ?? fixedCostTotal)} USD/ha</span>
          <span>{formatMaybe(row.totalCostUsdHa)} USD/ha</span>
        </div>
      ))}
    </div>
  )
}

const ReviewSection = () => {
  const {t} = useTranslation()
  const {
    calibrateYieldMap,
    cleanYieldMap,
    environmentCostPreview,
    fixedCostTotal,
    moistureTreatment,
    prescriptionSurfaceReference,
    selectedLand,
    surfaceReconciliation,
    yieldCorrection,
    yieldFieldName,
  } = useCreateEconomicReportContext()
  const calibrationValue =
    calibrateYieldMap && yieldCorrection.totalHarvestedTons !== ''
      ? `${formatMaybe(yieldCorrection.totalHarvestedTons)} t`
      : t('reports:economic.create.review.not_applied')
  const reconciliationStatus = (status) =>
    status
      ? t(
          `reports:economic.create.surface_reconciliation.statuses.${status}`,
          {defaultValue: status}
        )
      : '-'
  const prescriptionReferenceValue =
    prescriptionSurfaceReference.kind === 'inside_land' &&
    prescriptionSurfaceReference.outsideSurfaceHa > 0
      ? t(
          'reports:economic.create.surface_reconciliation.review_inside_with_outside',
          {
            outsideValue: formatMaybe(
              prescriptionSurfaceReference.outsideSurfaceHa
            ),
            value: formatMaybe(prescriptionSurfaceReference.surfaceHa),
          }
        )
      : prescriptionSurfaceReference.kind === 'inside_land'
      ? `${formatMaybe(prescriptionSurfaceReference.surfaceHa)} ha`
      : prescriptionSurfaceReference.kind === 'uploaded_pending_clip'
      ? t(
          'reports:economic.create.surface_reconciliation.review_uploaded_reference',
          {value: formatMaybe(prescriptionSurfaceReference.surfaceHa)}
        )
      : t(
          'reports:economic.create.surface_reconciliation.review_pending_reference'
        )

  return (
    <section className='economic-report-section'>
      <h6>{t('reports:economic.create.sections.review')}</h6>
      <div className='economic-report-review-summary'>
        <div className='economic-report-review-summary__group'>
          <strong className='economic-report-review-summary__title'>
            {t('reports:economic.create.review.group_data')}
          </strong>
          <dl>
            <div>
              <dt>{t('reports:economic.view.fields.selected_land')}</dt>
              <dd>{getLandLabel(selectedLand, {includeSurface: false})}</dd>
            </div>
            <div>
              <dt>{t('reports:economic.create.review.yield')}</dt>
              <dd>{yieldFieldName || '-'}</dd>
            </div>
            <div>
              <dt>{t('reports:economic.create.review.moisture')}</dt>
              <dd>{moistureTreatment.originalMoistureField || '-'}</dd>
            </div>
          </dl>
        </div>

        <div className='economic-report-review-summary__group'>
          <strong className='economic-report-review-summary__title'>
            {t('reports:economic.create.review.group_corrections')}
          </strong>
          <dl>
            <div>
              <dt>{t('reports:economic.create.review.cleaning')}</dt>
              <dd>
                {cleanYieldMap
                  ? t('reports:economic.create.review.applied')
                  : t('reports:economic.create.review.not_applied')}
              </dd>
            </div>
            <div>
              <dt>{t('reports:economic.create.review.calibration')}</dt>
              <dd>{calibrationValue}</dd>
            </div>
            <div>
              <dt>{t('reports:economic.create.review.commercial_moisture')}</dt>
              <dd>{formatMaybe(moistureTreatment.outputCommercialTargetPercent)} %</dd>
            </div>
          </dl>
        </div>

        <div className='economic-report-review-summary__group economic-report-review-summary__group--surfaces'>
          <strong className='economic-report-review-summary__title'>
            {t('reports:economic.create.review.group_surfaces')}
          </strong>
          <dl>
            <div>
              <dt>
                {t(
                  'reports:economic.create.surface_reconciliation.prescription_reference'
                )}
              </dt>
              <dd>{prescriptionReferenceValue}</dd>
            </div>
            <div>
              <dt>
                {t(
                  'reports:economic.create.surface_reconciliation.sowing_review'
                )}
              </dt>
              <dd>{reconciliationStatus(surfaceReconciliation.sowingStatus)}</dd>
            </div>
            {surfaceReconciliation.sowingStatus === 'differs' && (
              <div>
                <dt>
                  {t(
                    'reports:economic.create.surface_reconciliation.declared_sown_surface'
                  )}
                </dt>
                <dd>
                  {formatMaybe(surfaceReconciliation.declaredSownSurfaceHa)} ha
                </dd>
              </div>
            )}
            <div>
              <dt>
                {t(
                  'reports:economic.create.surface_reconciliation.harvest_review'
                )}
              </dt>
              <dd>{reconciliationStatus(surfaceReconciliation.harvestStatus)}</dd>
            </div>
            {surfaceReconciliation.harvestStatus === 'differs' && (
              <div>
                <dt>
                  {t(
                    'reports:economic.create.surface_reconciliation.declared_harvested_surface'
                  )}
                </dt>
                <dd>
                  {formatMaybe(surfaceReconciliation.declaredHarvestedSurfaceHa)} ha
                </dd>
              </div>
            )}
            <div>
              <dt>
                {t(
                  'reports:economic.create.surface_reconciliation.yield_map_coverage_review'
                )}
              </dt>
              <dd>
                {reconciliationStatus(
                  surfaceReconciliation.yieldMapCoverageStatus
                )}
              </dd>
            </div>
          </dl>
        </div>

      </div>
      <EnvironmentCostPreview rows={environmentCostPreview} fixedCostTotal={fixedCostTotal} />
      <small className='economic-report-help'>
        {t('reports:economic.create.hints.review')}
      </small>
    </section>
  )
}

const getWizardScrollContainer = (element) => {
  let currentElement = element?.parentElement

  while (currentElement && currentElement !== document.body) {
    const {overflowY} = window.getComputedStyle(currentElement)

    if (/(auto|scroll|overlay)/.test(overflowY)) {
      return currentElement
    }

    currentElement = currentElement.parentElement
  }

  return null
}

const resetWizardScroll = (element) => {
  if (!element) return

  const scrollContainer = getWizardScrollContainer(element)

  if (scrollContainer) {
    scrollContainer.scrollTop = 0
    return
  }

  element.scrollIntoView?.({behavior: 'auto', block: 'start', inline: 'nearest'})
}

const FormBody = ({currentStep, formBodyRef, mobile = false, wizardProps}) => (
  <div
    ref={formBodyRef}
    className={
      mobile ? 'reports-mobile-form economic-report-form' : 'modal-body economic-report-form'
    }
    onMouseDownCapture={blurActiveEconomicNumberInput}
    onTouchStartCapture={blurActiveEconomicNumberInput}
  >
    <EconomicReportWizard {...wizardProps} />
    {currentStep.id === 'general' && <GeneralSection />}
    {currentStep.id === 'land' && <LandSection />}
    {currentStep.id === 'prescription' && <PrescriptionSection />}
    {currentStep.id === 'yield' && <YieldMapSection part='fields' />}
    {currentStep.id === 'surfaces' && <YieldMapSection part='surfaces' />}
    {currentStep.id === 'corrections' && <YieldMapSection part='corrections' />}
    {currentStep.id === 'costs' && <CostsSection />}
    {currentStep.id === 'review' && <ReviewSection />}
  </div>
)

const CreateReportContent = ({activeStep, mobile = false, onClose, setActiveStep}) => {
  const {t} = useTranslation()
  const {loading, stepCompletion} = useCreateEconomicReportContext()
  const formBodyRef = useRef(null)
  const wizardSteps = useMemo(
    () => [
      {id: 'general', label: t('reports:economic.create.wizard.steps.general')},
      {id: 'land', label: t('reports:economic.create.wizard.steps.land')},
      {id: 'prescription', label: t('reports:economic.create.wizard.steps.prescription')},
      {id: 'yield', label: t('reports:economic.create.wizard.steps.yield')},
      {id: 'surfaces', label: t('reports:economic.create.wizard.steps.surfaces')},
      {id: 'corrections', label: t('reports:economic.create.wizard.steps.corrections')},
      {id: 'costs', label: t('reports:economic.create.wizard.steps.costs')},
      {id: 'review', label: t('reports:economic.create.wizard.steps.review')},
    ],
    [t]
  )
  const activeStepIndex = Math.max(
    wizardSteps.findIndex((step) => step.id === activeStep),
    0
  )
  const currentStep = wizardSteps[activeStepIndex] || wizardSteps[0]
  const isLastStep = activeStepIndex === wizardSteps.length - 1
  const getStepIsComplete = (stepId) => Boolean(stepCompletion?.[stepId])
  const canOpenStep = (stepIndex) =>
    stepIndex <= activeStepIndex ||
    wizardSteps.slice(0, stepIndex).every((step) => getStepIsComplete(step.id))
  const canGoNext = getStepIsComplete(currentStep.id)

  useLayoutEffect(() => {
    resetWizardScroll(formBodyRef.current)
  }, [activeStep, mobile])

  const goToPreviousStep = () => {
    const previousStep = wizardSteps[activeStepIndex - 1]

    if (previousStep) {
      setActiveStep(previousStep.id)
    }
  }

  const goToNextStep = () => {
    const nextStep = wizardSteps[activeStepIndex + 1]

    if (nextStep && canGoNext) {
      setActiveStep(nextStep.id)
    }
  }

  const wizardProps = {
    activeStepIndex,
    canOpenStep,
    getStepIsComplete,
    wizardSteps,
  }
  const footerProps = {
    activeStepIndex,
    canGoNext,
    isLastStep,
    onNext: goToNextStep,
    onPrevious: goToPreviousStep,
  }

  if (loading) {
    return (
      <div className={mobile ? 'reports-mobile-card__body' : 'p-5'}>
        <Loading loadingText={t('reports:economic.create.creating')} />
      </div>
    )
  }

  if (mobile) {
    return (
      <>
        <FormHeader mobile />
        <div className='reports-mobile-card__body reports-mobile-card__body--form'>
          <FormBody
            currentStep={currentStep}
            formBodyRef={formBodyRef}
            mobile
            wizardProps={wizardProps}
          />
          <FormFooter mobile onClose={onClose} {...footerProps} />
        </div>
      </>
    )
  }

  return (
    <>
      <FormHeader />
      <FormBody
        currentStep={currentStep}
        formBodyRef={formBodyRef}
        wizardProps={wizardProps}
      />
      <FormFooter onClose={onClose} {...footerProps} />
    </>
  )
}

const CreateEconomicReportContainer = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {activeStep, setActiveStep} = useCreateEconomicReportContext()

  const closeReport = () => navigate('/gis/reports/economic')
  const reportContentProps = {
    activeStep,
    onClose: closeReport,
    setActiveStep,
  }

  return isMobile ? (
    <div className='reports-mobile-shell displayable'>
      <div
        className='card shadow-sm right-card-data reports-mobile-card reports-mobile-card--editor'
        onMouseDownCapture={blurActiveEconomicNumberInput}
        onTouchStartCapture={blurActiveEconomicNumberInput}
      >
        <CreateReportContent mobile {...reportContentProps} />
      </div>
    </div>
  ) : (
    <Modal open={true} focus={false}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div
          className='modal-content'
          onMouseDownCapture={blurActiveEconomicNumberInput}
          onTouchStartCapture={blurActiveEconomicNumberInput}
        >
          <CreateReportContent {...reportContentProps} />
        </div>
      </div>
    </Modal>
  )
}

export default CreateEconomicReportContainer
